import React, { useState, useRef, useEffect, useCallback } from 'react';

const GREETING = "Hi there 👋 What brings you to Salesyo?";

function LiveChat() {
  // messages: array of { role: 'user'|'assistant', content }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => 'sess_' + Math.random().toString(36).slice(2));
  const convIdRef = useRef(null);

  const bodyRef = useRef(null);
  const textRef = useRef(null);

  const scrollDown = () => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useEffect(() => { scrollDown(); }, [messages, typing, error]);

  const startChat = useCallback(() => {
    setMessages([]);
    setError('');
    setInput('');
    convIdRef.current = null;
    setTyping(true);
    const t = setTimeout(() => {
      setTyping(false);
      setMessages([{ role: 'assistant', content: GREETING }]);
    }, 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const cleanup = startChat();
    return cleanup;
  }, [startChat]);

  const sendMessage = async () => {
    const val = input.trim();
    if (!val || busy) return;

    setError('');
    setBusy(true);

    const userMsg = { role: 'user', content: val };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    if (textRef.current) textRef.current.style.height = 'auto';

    setTimeout(() => setTyping(true), 350);
    try {
      // The upstream API is stateful: send a single message plus the
      // conversation_id returned by the first reply so it loads the history.
      const endpoint = process.env.REACT_APP_CHAT_ENDPOINT || '/api/chat.php';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: val,
          conversation_id: convIdRef.current,
          visitor_ref: sessionId
        })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (data.conversation_id) convIdRef.current = data.conversation_id;
      const reply = (data.reply || '').trim();
      setTyping(false);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: reply || "Sorry, I didn't quite catch that. Could you rephrase?" }
      ]);
    } catch (e) {
      setTyping(false);
      setError("⚠️ The assistant isn't responding right now. Try again in a moment.");
    } finally {
      setBusy(false);
      if (textRef.current) textRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 90) + 'px';
  };

  const showReplay = messages.filter(m => m.role === 'user').length >= 1;

  return (
    <div className="chat">
      <div className="chat-head">
        <div className="chat-av">SY</div>
        <div>
          <div className="nm">Salesyo</div>
          <div className="st"><span className="d"></span>replies in ~40s</div>
        </div>
        <div className="live">● LIVE</div>
      </div>
      <div className="chat-body" ref={bodyRef}>
        {messages.map((m, i) => (
          <div key={i} className={`msg ${m.role === 'user' ? 'user' : 'ai'}`}>{m.content}</div>
        ))}
        {typing && (
          <div className="typing"><span></span><span></span><span></span></div>
        )}
        {error && <div className="chat-err">{error}</div>}
      </div>
      <div className="chat-foot">
        <div className="chat-input">
          <textarea
            ref={textRef}
            rows={1}
            placeholder="Ask your question…"
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
          />
          <button
            className="chat-send"
            onClick={sendMessage}
            disabled={!input.trim() || busy}
            aria-label="Send"
          >↑</button>
        </div>
        {showReplay && (
          <button className="chat-replay" onClick={startChat}>↻ Start over</button>
        )}
      </div>
    </div>
  );
}

export default LiveChat;
